// Voice announcement queue for the visitor queue display.
//
// Announcements are spoken as soon as visitors are enqueued instead of
// waiting for a polling tick. A watchdog timeout and an onerror handler
// guarantee the queue can never deadlock when the browser's speech engine
// misbehaves (iOS Safari is known to drop onend, or to silently block
// speech until the user has interacted with the page).

export function createAnnouncer({
  speechSynthesis = window.speechSynthesis,
  SpeechSynthesisUtterance = window.SpeechSynthesisUtterance,
  buildText = (v) => `Now serving, ${v.name}. Please go to ${v.meetingDesk}`,
  getVoice = () => null,
  // longest we wait for the speech engine before moving on
  maxSpeechMs = 15000,
  postSpeechPauseMs = 1000,
  onSpeechStart = () => {},
} = {}) {
  const state = { queue: [], busy: false };

  function enqueue(visitors = []) {
    let added = false;
    visitors.forEach((v) => {
      if (v.meetingStarted && !v.meetingEnded) {
        if (!state.queue.find((q) => q.id === v.id)) {
          state.queue.push({ ...v, processed: false });
          added = true;
        }
      }
    });
    if (added) processQueue();
    return added;
  }

  async function processQueue() {
    if (state.busy) return;
    state.busy = true;
    try {
      let next;
      while ((next = state.queue.find((q) => !q.processed))) {
        next.processed = true;
        await speak(next);
      }
    } finally {
      state.busy = false;
    }
  }

  function speak(visitor) {
    return new Promise((resolve) => {
      const text = buildText(visitor);
      console.log(text);
      let settled = false;
      let watchdog;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        setTimeout(() => resolve(true), postSpeechPauseMs);
      };
      watchdog = setTimeout(finish, maxSpeechMs);
      try {
        const msg = new SpeechSynthesisUtterance();
        const voice = getVoice();
        if (voice) msg.voice = voice;
        msg.voiceURI = 'native';
        msg.volume = 1;
        msg.rate = 1;
        msg.pitch = 2;
        msg.text = text;
        msg.lang = 'en-US';
        msg.onstart = () => onSpeechStart();
        msg.onend = finish;
        msg.onerror = finish;
        speechSynthesis.speak(msg);
      } catch (e) {
        console.log('Speech failed:', e);
        finish();
      }
    });
  }

  function isBusy() {
    return state.busy;
  }

  return { enqueue, processQueue, isBusy };
}
